import { useCallback, useEffect, useRef, useState } from "react";
import { log } from "@/helpers";

interface VersionPayload {
  buildTime?: string;
  timestamp?: number | string;
}

const VERSION_ENDPOINT = "/version.json";

function getVersionKey(payload: VersionPayload): string | null {
  if (payload.buildTime) {
    return payload.buildTime;
  }

  if (payload.timestamp) {
    return String(payload.timestamp);
  }

  return null;
}

export function useVersionCheck(intervalMs = 60000) {
  const currentVersion = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const checkVersion = useCallback(async () => {
    try {
      const response = await fetch(VERSION_ENDPOINT, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      if (!response.ok) {
        return;
      }

      const payload = (await response.json()) as VersionPayload;
      const nextVersion = getVersionKey(payload);

      if (!nextVersion) {
        return;
      }

      if (!currentVersion.current) {
        currentVersion.current = nextVersion;
        return;
      }

      if (currentVersion.current !== nextVersion) {
        setUpdateAvailable(true);
      }
    } catch (error) {
      log("useVersionCheck", "Version check request failed", { error });
    }
  }, []);

  useEffect(() => {
    if (updateAvailable) {
      return;
    }

    void checkVersion();

    const intervalId = globalThis.setInterval(() => {
      void checkVersion();
    }, intervalMs);

    return () => {
      globalThis.clearInterval(intervalId);
    };
  }, [checkVersion, intervalMs, updateAvailable]);

  const reload = useCallback(() => {
    globalThis.location.reload();
  }, []);

  return {
    updateAvailable,
    reload,
  };
}
